import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import shipmentIcon from '../../images/shipment_black.svg';
import { getFullUrl } from '../../utils/utils';
import { usePricingRates } from '../../hooks/usePricingRates';
import { applyCruiseParams } from '../../services/CruiseParamsService';

const fetchAutoComplete = async (term, limitToCityState) => {
  const sessionToken = Math.random().toString(36).substring(2) +
    (new Date()).getTime().toString(36);

  const params = new URLSearchParams({
    sessiontoken: sessionToken,
    term,
    brand: window.gon.current_brand,
    limit_to_city_state: limitToCityState
  });

  if (window.gon?.partner_id && window.gon?.limit_autocomplete_to_partner) {
    params.append('partner_id', window.gon.partner_id);
    params.append('partner_autocomplete_results', 'true');
  }

  if (window.user_ident) {
    params.append('user_id', window.user_ident);
  }

  const url = `${window.gon.autocomplete_all_endpoint}?${params.toString()}`;
  const apiEndpoint = getFullUrl(url);

  const response = await fetch(apiEndpoint, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' }
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.message || `Error: ${response.status}`);
  return data;
};

const LocationInput = ({ id, placeholder, onSelect, onClear, limitToCityState, lockedValue, disabled }) => {
  const [inputValue, setInputValue] = useState(lockedValue || '');
  const [results, setResults] = useState(null);
  const [focused, setFocused] = useState(false);
  const [isSelected, setIsSelected] = useState(!!lockedValue);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const handleBlur = () => {
    document.addEventListener('click', (event) => {
      if (listRef.current && !listRef.current.contains(event.target) &&
          inputRef.current !== event.target) {
        setFocused(false);
      }
    }, { once: true });
  };

  const handleSelect = (item) => {
    setIsSelected(true);
    setInputValue(item.formatted_address?.split('<br>')[0] || '');
    setFocused(false);
    onSelect(item);
  };

  const debouncedFetch = useCallback(
    debounce(async (value) => {
      if (value.length > 2) {
        setLoading(true);
        try {
          const data = await fetchAutoComplete(value, limitToCityState);
          setResults(data);
        } catch (error) {
          console.error('Autocomplete error:', error);
        } finally {
          setLoading(false);
        }
      }
    }, 500, { leading: false, trailing: true }),
    []
  );

  useEffect(() => {
    if (inputValue && !isSelected) {
      debouncedFetch(inputValue);
    }
  }, [inputValue, debouncedFetch, isSelected]);

  return (
    <div className="relative">
      <input
        ref={inputRef}
        id={id}
        type="text"
        value={inputValue}
        autoComplete="off"
        placeholder={placeholder}
        disabled={disabled}
        readOnly={disabled}
        className={`text-xl leading-7 text-[#18293a] placeholder-[#bfbfbf] outline-none bg-transparent w-full
          ${disabled ? 'cursor-not-allowed opacity-80' : ''}`}
        onChange={(e) => {
          if (disabled) return;
          setIsSelected(false);
          setInputValue(e.target.value);
          if (e.target.value.length === 0) setResults(null);
          if (onClear) onClear();
        }}
        onFocus={() => { if (!disabled) setFocused(true); }}
        onBlur={handleBlur}
      />
      {loading && (
        <div className="absolute right-0 top-1/2 -translate-y-1/2">
          <div className="w-5 h-5 border-2 border-[#02a40b] border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      {focused && results && results.length > 0 && (
        <div
          ref={listRef}
          className="absolute left-0 right-0 top-full mt-2 bg-white rounded-lg shadow-lg
                     border border-gray-200 max-h-60 overflow-auto z-50"
        >
          <ul>
            {results.map((item, index) => (
              <li
                key={index}
                onClick={() => handleSelect(item)}
                className="px-4 py-3 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
              >
                {item.icon_url && (
                  <img src={item.icon_url} width="18" height="18" className="flex-none" alt="" />
                )}
                <span className="text-sm text-[#18293a] text-left">{item.formatted_address?.split('<br>')[0]}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

LocationInput.propTypes = {
  id: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onClear: PropTypes.func,
  limitToCityState: PropTypes.bool,
  lockedValue: PropTypes.string,
  disabled: PropTypes.bool,
};

const trackWidgetEvent = (event, extraProps = {}) => {
  try {
    window.rudderanalytics?.track(event, {
      brand: window.gon?.current_brand || 'shipsticks',
      ...extraProps
    });
  } catch (e) {
    // silently ignore tracking failures
  }
};

const BookingWidget = ({
  price = '',
  ctaText = "Let's Go",
  shipFromPlaceholder,
  shipToPlaceholder,
  limitToCityState = false,
  subHeading,
  url,
  address,
  lockOriginOutboundLeg = false,
  lockDestinationOutboundLeg = false,
  lockOriginReturnLeg = false,
  lockDestinationReturnLeg = false,
  addressIsEditable = false,
  subDomain,
  couponCode,
  coupon
}) => {
  const effectiveCoupon = coupon || couponCode;
  const fromPlaceholder = shipFromPlaceholder || (limitToCityState ? 'Miami, FL' : 'Where from?');
  const toPlaceholder = shipToPlaceholder || (limitToCityState ? 'Amsterdam, NL' : 'Where to?');
  // partnerData is camelCased via humps, so nested keys like `formatted_address`
  // become `formattedAddress`. Normalize back to the snake_case shape the rest
  // of the widget (pricing, tracking, shipParams builder) expects.
  const normalizedAddress = address
    ? {
      ...address,
      _id: address._id || address.id,
      _type: address._type || address.type,
      formatted_address: address.formatted_address || address.formattedAddress,
      icon_url: address.icon_url || address.iconUrl,
    }
    : null;
  const hasAddress = !!(normalizedAddress && normalizedAddress.formatted_address);
  const prefillFrom = hasAddress && lockOriginOutboundLeg;
  const prefillTo = hasAddress && lockDestinationOutboundLeg;
  const lockFrom = prefillFrom && !addressIsEditable;
  const lockTo = prefillTo && !addressIsEditable;
  const [fromLocation, setFromLocation] = useState(prefillFrom ? normalizedAddress : null);
  const [toLocation, setToLocation] = useState(prefillTo ? normalizedAddress : null);
  const [startingPrice, setStartingPrice] = useState(null);
  const [shipParams, setShipParams] = useState(null);
  const { getPricingRates, loading: pricingLoading } = usePricingRates();

  useEffect(() => {
    if (!fromLocation || !toLocation) return;

    const fetchRates = async () => {
      const toShipPoint = (location) => {
        const type = location._type || 'GooglePlace';
        return { id: location._id, type };
      };

      const result = await getPricingRates({
        origin: toShipPoint(fromLocation),
        destination: toShipPoint(toLocation),
      });

      if (result.success && result.data) {
        const { rates, addresses } = result.data;

        // Find lowest luggage price
        if (rates) {
          let lowest = Infinity;
          rates.forEach((rate) => {
            rate.products?.forEach((product) => {
              const line = product.product_line_filter_key || product.product_line;
              if (line === 'Luggage' && product.price_cents && product.price_cents < lowest) {
                lowest = product.price_cents;
              }
            });
          });
          if (lowest < Infinity) {
            setStartingPrice(Math.floor(lowest / 100));
          }
        }

        // Build ship params from pricing response addresses
        if (addresses) {
          const params = new URLSearchParams();
          const o = addresses.origin || {};
          const d = addresses.destination || {};

          // Business/POI selections carry their name (e.g. "Chipotle Mexican
          // Grill"). Plain addresses/cities have no 'establishment' sub_type —
          // never send those as a company. The autocomplete object is the
          // primary source: the pricing response only carries a company when
          // the backend uses the Place Details API, and environments with the
          // use_geocoder_google_places flag enabled (production) resolve
          // places via the Geocoding API, which has no business names.
          const companyFromLocation = (loc) =>
            (loc?.name && (loc.sub_types || []).includes('establishment') ? loc.name : null);

          if (fromLocation._type === 'Club' || fromLocation._type === 'SkiResort') {
            params.set('shipping_from', fromLocation._id);
          } else {
            if (o.o_zip) params.set('o_zip', o.o_zip);
            if (o.o_city) params.set('o_city', o.o_city);
            if (o.o_state) params.set('o_state', o.o_state);
            if (o.o_country) params.set('o_country', o.o_country);
            if (o.o_address) params.set('o_address', o.o_address);
            const oCompany = companyFromLocation(fromLocation) || o.o_company;
            if (oCompany) params.set('o_company', oCompany);
          }
          if (toLocation._type === 'Club' || toLocation._type === 'SkiResort') {
            params.set('shipping_to', toLocation._id);
          } else {
            if (d.d_zip) params.set('d_zip', d.d_zip);
            if (d.d_city) params.set('d_city', d.d_city);
            if (d.d_state) params.set('d_state', d.d_state);
            if (d.d_country) params.set('d_country', d.d_country);
            if (d.d_address) params.set('d_address', d.d_address);
            const dCompany = companyFromLocation(toLocation) || d.d_company;
            if (dCompany) params.set('d_company', dCompany);
          }

          setShipParams(params.toString());
        }
      }
    };

    fetchRates();
  }, [fromLocation, toLocation]);

  const handleCtaClick = () => {
    trackWidgetEvent('tac_booking_checkout_started');
    const rawBase = url || '/ship';

    const [basePath, baseQuery = ''] = rawBase.split('?');
    const currentParams = new URLSearchParams(window.location.search);
    const params = new URLSearchParams(baseQuery);

    if (shipParams) {
      new URLSearchParams(shipParams).forEach((value, key) => {
        params.set(key, value);
      });
    }

    // Preserve TAC and other important params from the current URL
    const preserveKeys = [
      'euid_source', 'euid', 'bags',
      'agent_id', 'ta_name', 'ta_email', 'ta_network', 'ta_company',
      'ta_agency', 'iata_number', 'utm_source', 'utm_medium', 'utm_campaign',
      'coupon',
      // Address params (GSE-10993)
      'o_address', 'o_city', 'o_state', 'o_zip', 'o_country', 'o_company', 'o_apt',
      'd_address', 'd_city', 'd_state', 'd_zip', 'd_country', 'd_company', 'd_apt',
      // Facility params
      'shipping_from', 'shipping_to', 'o_facility_id', 'd_facility_id',
      // Cruise params
      'o_cruise_line', 'o_cruise_port', 'o_cruise_itinerary',
      'd_cruise_line', 'd_cruise_port', 'd_cruise_itinerary',
      // Product pre-population
      'products'
    ];

    preserveKeys.forEach(key => {
      const value = currentParams.get(key);
      if (value && !params.has(key)) {
        params.set(key, value);
      }
    });

    if (lockFrom) params.set('lock_from', 'true');
    if (lockTo) params.set('lock_to', 'true');
    if (lockOriginOutboundLeg) params.set('lock_origin_outbound_leg', 'true');
    if (lockDestinationOutboundLeg) params.set('lock_destination_outbound_leg', 'true');
    if (lockOriginReturnLeg) params.set('lock_origin_return_leg', 'true');
    if (lockDestinationReturnLeg) params.set('lock_destination_return_leg', 'true');
    if (addressIsEditable) params.set('address_is_editable', 'true');
    applyCruiseParams(params, currentParams);
    if (subDomain) {
      params.set('partner', subDomain);
    }

    // URL coupon prop overrides page-level couponCode
    if (effectiveCoupon) {
      params.set('coupon', effectiveCoupon);
    }

    const queryString = params.toString();
    const path = queryString ? `${basePath}?${queryString}` : basePath;
    window.location.href = getFullUrl(path, window.gon?.subdomain);
  };

  const displayPrice = startingPrice || (price && parseFloat(price) > 0 ? price : 49);
  const hasPrice = !!displayPrice;

  return (
    <div className="backdrop-blur-sm bg-[rgba(24,41,58,0.7)] flex
    flex-col items-stretch rounded-2xl shadow-[0px_4px_8px_0px_rgba(0,0,0,0.16)] p-1 w-full">
      <div className="bg-white flex flex-col md:flex-row md:items-center gap-4 p-4 md:pl-6 md:pr-2 md:py-2 rounded-xl">
        <div className="flex flex-col flex-1 min-w-0 border border-gray-300
                        rounded-lg p-3 md:border-0 md:rounded-none md:p-0">
          <label className="font-medium text-sm leading-5 text-[#18293a] text-left">Ship from</label>
          <LocationInput
            id="partner-ship-from"
            placeholder={fromPlaceholder}
            onSelect={(item) => {
              setFromLocation(item);
              trackWidgetEvent('tac_landing_widget_origin_entered', {
                origin_entered: item.formatted_address
              });
            }}
            onClear={() => { setFromLocation(null); setStartingPrice(null); setShipParams(null); }}
            limitToCityState={limitToCityState}
            lockedValue={prefillFrom ? normalizedAddress.formatted_address : undefined}
            disabled={lockFrom}
          />
        </div>

        <div className="flex flex-col flex-1 min-w-0 border border-gray-300
                        rounded-lg p-3 md:border-0 md:rounded-none md:p-0">
          <label className="font-medium text-sm leading-5 text-[#18293a] text-left">Ship to</label>
          <LocationInput
            id="partner-ship-to"
            placeholder={toPlaceholder}
            onSelect={(item) => {
              setToLocation(item);
              trackWidgetEvent('tac_landing_widget_destination_entered', {
                destination_entered: item.formatted_address
              });
            }}
            onClear={() => { setToLocation(null); setStartingPrice(null); setShipParams(null); }}
            limitToCityState={limitToCityState}
            lockedValue={prefillTo ? normalizedAddress.formatted_address : undefined}
            disabled={lockTo}
          />
        </div>

        <div className="flex flex-row flex-wrap items-baseline gap-x-1 md:flex-col md:gap-x-0 shrink-0">
          <span className="font-medium text-sm leading-5 text-[#18293a] text-left">Starting at</span>
          {pricingLoading ? (
            <div className="flex items-center h-7">
              <div className="w-5 h-5 border-2 border-[#02a40b] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : hasPrice ? (
            <span className="leading-7">
              <span className="text-xl text-[#02a40b]">${displayPrice} </span>
              <span className="text-sm text-[#bfbfbf]">each way</span>
            </span>
          ) : (
            <span className="text-xl leading-7 text-[#bfbfbf]">&mdash;</span>
          )}
        </div>

        <button
          type="button"
          onClick={handleCtaClick}
          className={`flex items-center justify-center gap-3 px-6 h-16 rounded-md
            text-white font-semibold text-base leading-6 w-full md:w-auto md:shrink-0 md:ml-auto
            transition-colors bg-[#02a40b] hover:bg-[#028a09] cursor-pointer`}
        >
          <span className="text-black">{ctaText}</span>
          <img src={shipmentIcon} alt="" width="32" height="32" />
        </button>
      </div>

      <div className="px-4 py-2 text-center">
        <span className="font-medium text-sm leading-5 text-white">{subHeading}</span>
      </div>
    </div>
  );
};

BookingWidget.propTypes = {
  price: PropTypes.string,
  phoneNumber: PropTypes.string,
  ctaText: PropTypes.string,
  shipFromPlaceholder: PropTypes.string,
  shipToPlaceholder: PropTypes.string,
  limitToCityState: PropTypes.bool,
  subHeading: PropTypes.string,
  url: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  address: PropTypes.object,
  lockOriginOutboundLeg: PropTypes.bool,
  lockDestinationOutboundLeg: PropTypes.bool,
  lockOriginReturnLeg: PropTypes.bool,
  lockDestinationReturnLeg: PropTypes.bool,
  addressIsEditable: PropTypes.bool,
  subDomain: PropTypes.string,
  couponCode: PropTypes.string,
  coupon: PropTypes.string,
};

export default BookingWidget;
